import React from 'react';
import { Percent, Users, Info, Package, Database, Layers, Wrench, ChevronDown, ChevronRight, Plus, Minus } from 'lucide-react';
import InfoTooltip from '../../../components/common/InfoTooltip';

/**
 * Discounts Step - Category and line-item discount application
 * Organized by category groups matching Initial Review
 */
const DiscountsStep = ({ quoteData, setQuoteData, items, calculatePEPM }) => {

  // Category groups matching Initial Review
  const categoryGroups = {
    platform: {
      label: 'Platform & Deployment',
      icon: Package,
      color: 'blue',
      categories: ['platform']
    },
    users: {
      label: 'User Licenses',
      icon: Users,
      color: 'indigo',
      categories: ['users']
    },
    erpConnectors: {
      label: 'ERP Connectors',
      icon: Database,
      color: 'purple',
      categories: ['erpConnectors']
    },
    products: {
      label: 'Products',
      icon: Layers,
      color: 'teal',
      categories: ['reportsEBS', 'reportsCloud', 'financialAnalytics', 'peopleAnalytics', 'glConnect', 'dataPipelines', 'utilities'],
      subCategories: {
        reports: {
          label: 'Reports',
          categories: ['reportsEBS', 'reportsCloud']
        },
        analytics: {
          label: 'Analytics',
          categories: ['financialAnalytics', 'peopleAnalytics']
        },
        pipelines: {
          label: 'Data Pipelines',
          categories: ['dataPipelines']
        },
        glUtilities: {
          label: 'GL Connect & Utilities',
          categories: ['glConnect', 'utilities']
        }
      }
    },
    services: {
      label: 'Services',
      icon: Wrench,
      color: 'orange',
      categories: ['services']
    }
  };

  const [expandedGroups, setExpandedGroups] = React.useState({
    platform: true,
    users: true,
    erpConnectors: false,
    products: true,
    services: false
  });

  const [expandedSubCategories, setExpandedSubCategories] = React.useState({
    reports: true,
    analytics: true,
    pipelines: false,
    glUtilities: false
  });

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const toggleSubCategory = (subKey) => {
    setExpandedSubCategories(prev => ({ ...prev, [subKey]: !prev[subKey] }));
  };

  // Group items by category groups
  const groupedItems = React.useMemo(() => {
    const groups = {};
    Object.keys(categoryGroups).forEach(groupKey => {
      groups[groupKey] = items.filter(item =>
        categoryGroups[groupKey].categories.includes(item.category)
      );
    });
    return groups;
  }, [items]);

  const setLineItemDiscount = (sku, value) => {
    setQuoteData({
      ...quoteData,
      lineItemDiscounts: {
        ...quoteData.lineItemDiscounts,
        [sku]: Math.min(100, Math.max(0, parseInt(value) || 0))
      }
    });
  };

  const clearLineItemDiscount = (sku) => {
    const updated = { ...quoteData.lineItemDiscounts };
    delete updated[sku];
    setQuoteData({
      ...quoteData,
      lineItemDiscounts: updated
    });
  };

  // Set category-level discount (applies to all categories in a group)
  const setCategoryDiscount = (categories, value) => {
    const discountValue = Math.min(100, Math.max(0, parseInt(value) || 0));
    const updated = { ...quoteData.categoryDiscounts };
    categories.forEach(cat => {
      updated[cat] = discountValue;
    });
    setQuoteData({
      ...quoteData,
      categoryDiscounts: updated
    });
  };

  // Clear category-level discount
  const clearCategoryDiscount = (categories) => {
    const updated = { ...quoteData.categoryDiscounts };
    categories.forEach(cat => {
      delete updated[cat];
    });
    setQuoteData({
      ...quoteData,
      categoryDiscounts: updated
    });
  };

  // Get the category discount for a group (returns the discount if all categories have the same, otherwise 0)
  const getGroupCategoryDiscount = (categories) => {
    const discounts = categories.map(cat => quoteData.categoryDiscounts?.[cat] || 0);
    const firstDiscount = discounts[0];
    return discounts.every(d => d === firstDiscount) ? firstDiscount : 0;
  };

  const getEffectiveDiscount = (item) => {
    const lineDiscount = quoteData.lineItemDiscounts?.[item.sku] || 0;
    const catDiscount = quoteData.categoryDiscounts?.[item.category] || 0;
    return lineDiscount > 0 ? lineDiscount : catDiscount;
  };

  const calculateItemTotal = (item) => {
    const subtotal = item.listPrice * item.quantity;
    const discount = getEffectiveDiscount(item) / 100;
    return subtotal * (1 - discount);
  };

  // Calculate group subtotals
  const getGroupSubtotal = (groupKey) => {
    return groupedItems[groupKey].reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const getGroupListTotal = (groupKey) => {
    return groupedItems[groupKey].reduce((sum, item) => sum + (item.listPrice * item.quantity), 0);
  };

  // Get items for a sub-category
  const getSubCategoryItems = (subCategories) => {
    return items.filter(item => subCategories.includes(item.category));
  };

  // Get sub-category totals
  const getSubCategoryListTotal = (subCategories) => {
    return getSubCategoryItems(subCategories).reduce((sum, item) => sum + (item.listPrice * item.quantity), 0);
  };

  const getSubCategoryDiscountedTotal = (subCategories) => {
    return getSubCategoryItems(subCategories).reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const colorClasses = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'bg-blue-600' },
    indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', icon: 'bg-indigo-600' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', icon: 'bg-purple-600' },
    teal: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', icon: 'bg-teal-600' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: 'bg-orange-600' },
  };

  // Get PEPM data to check if HCM deal
  const pepmData = calculatePEPM ? calculatePEPM() : null;
  const isHCMDeal = !!pepmData;
  const employeeCount = pepmData?.employeeCount || 0;

  // Calculate PEPM for a line item (using discounted price)
  const calculateItemPEPM = (item) => {
    if (!isHCMDeal || item.isOneTime || employeeCount === 0) return null;
    const itemTotal = calculateItemTotal(item);
    return (itemTotal / (employeeCount * 12)).toFixed(2);
  };

  // Calculate PEPM for a group (ARR items only, using discounted prices)
  const getGroupPEPM = (groupKey) => {
    if (!isHCMDeal || employeeCount === 0) return null;
    const arrItems = groupedItems[groupKey].filter(item => !item.isOneTime);
    const arrTotal = arrItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);
    return (arrTotal / (employeeCount * 12)).toFixed(2);
  };

  // Calculate PEPM for a sub-category (ARR items only, using discounted prices)
  const getSubCategoryPEPM = (subCategories) => {
    if (!isHCMDeal || employeeCount === 0) return null;
    const arrItems = getSubCategoryItems(subCategories).filter(item => !item.isOneTime);
    const arrTotal = arrItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);
    return (arrTotal / (employeeCount * 12)).toFixed(2);
  };

  // Render discount table for items
  const renderDiscountTable = (itemsList) => (
    <table className="w-full">
      <thead>
        <tr className="border-b border-slate-100">
          <th className="text-left py-2 px-4 text-xs font-semibold text-slate-500 w-28">SKU</th>
          <th className="text-left py-2 px-4 text-xs font-semibold text-slate-500">Item</th>
          <th className="text-center py-2 px-4 text-xs font-semibold text-slate-500 w-14">Qty</th>
          <th className="text-right py-2 px-4 text-xs font-semibold text-slate-500 w-24">List</th>
          <th className="text-center py-2 px-4 text-xs font-semibold text-slate-500 w-32">Discount %</th>
          <th className="text-right py-2 px-4 text-xs font-semibold text-slate-500 w-24">Final</th>
          {isHCMDeal && (
            <th className="text-right py-2 px-4 text-xs font-semibold text-purple-600 w-24">PEPM</th>
          )}
        </tr>
      </thead>
      <tbody>
        {itemsList.map((item, idx) => {
          const effectiveDiscount = getEffectiveDiscount(item);
          const finalPrice = calculateItemTotal(item);
          const hasLineDiscount = (quoteData.lineItemDiscounts?.[item.sku] || 0) > 0;
          const itemPepm = calculateItemPEPM(item);

          return (
            <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50">
              <td className="py-2 px-4 text-xs font-mono text-slate-500">{item.sku}</td>
              <td className="py-2 px-4 text-sm text-slate-800">
                {item.name}
                {item.isPEPM && (
                  <span className="ml-2 text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                    PEPM
                  </span>
                )}
                {item.isOneTime && (
                  <span className="ml-2 text-xs text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">
                    One-time
                  </span>
                )}
              </td>
              <td className="py-2 px-4 text-sm text-center text-slate-600">{item.quantity}</td>
              <td className="py-2 px-4 text-sm text-right text-slate-700">
                ${(item.listPrice * item.quantity).toLocaleString()}
              </td>
              <td className="py-2 px-4">
                <div className="flex items-center gap-1">
                  <div className="relative flex-1">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={quoteData.lineItemDiscounts?.[item.sku] || ''}
                      onChange={(e) => setLineItemDiscount(item.sku, e.target.value)}
                      className={`w-full p-1.5 pr-7 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        hasLineDiscount
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-300'
                      }`}
                      placeholder="0"
                    />
                    <Percent size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  </div>
                  {hasLineDiscount && (
                    <button
                      onClick={() => clearLineItemDiscount(item.sku)}
                      className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Clear discount"
                    >
                      ×
                    </button>
                  )}
                </div>
              </td>
              <td className="py-2 px-4 text-sm text-right">
                <div className="font-semibold text-slate-800">
                  ${finalPrice.toLocaleString()}
                </div>
                {effectiveDiscount > 0 && (
                  <div className="text-xs text-red-600">-{effectiveDiscount}%</div>
                )}
              </td>
              {isHCMDeal && (
                <td className="py-2 px-4 text-sm text-right font-medium text-purple-600">
                  {itemPepm ? `$${itemPepm}` : '-'}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
        Apply Discounts
        <InfoTooltip
          text="Category discounts apply to all items in a group (green input). Line-item discounts override the category discount for specific items. Discounts exceeding thresholds require manager approval."
          position="right"
        />
      </h2>
      <p className="text-slate-600 mb-6">Set category-level or line-item specific discounts.</p>

      {/* Info banner */}
      <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
        <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p><strong>Category discount:</strong> Applies to all items in a category (green input in header)</p>
          <p className="mt-1"><strong>Line item discount:</strong> Overrides category discount for specific items</p>
        </div>
      </div>

      {/* Grouped Line Items */}
      <div className="space-y-4">
        {Object.entries(categoryGroups).map(([groupKey, group]) => {
          const groupItems = groupedItems[groupKey];
          if (groupItems.length === 0) return null;

          const Icon = group.icon;
          const colors = colorClasses[group.color];
          const isExpanded = expandedGroups[groupKey];
          const listTotal = getGroupListTotal(groupKey);
          const discountedTotal = getGroupSubtotal(groupKey);
          const totalDiscount = listTotal - discountedTotal;
          const hasSubCategories = group.subCategories;
          const groupPepm = getGroupPEPM(groupKey);

          const categoryDiscount = getGroupCategoryDiscount(group.categories);
          const hasCategoryDiscount = categoryDiscount > 0;

          return (
            <div key={groupKey} className={`rounded-xl border-2 ${colors.border} overflow-hidden`}>
              {/* Group Header */}
              <div className={`${colors.bg} px-4 py-3`}>
                <div className="flex items-center justify-between">
                  {/* Left side - expand button and label */}
                  <button
                    onClick={() => toggleGroup(groupKey)}
                    className="flex items-center gap-3 hover:opacity-80"
                  >
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    <div className={`w-8 h-8 ${colors.icon} rounded-lg flex items-center justify-center`}>
                      <Icon size={16} className="text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className={`font-semibold ${colors.text}`}>{group.label}</h3>
                      <span className="text-xs text-slate-500">{groupItems.length} items</span>
                    </div>
                  </button>

                  {/* Right side - category discount, totals, PEPM */}
                  <div className="flex items-center gap-4">
                    {/* Category Discount Input */}
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <span className="text-xs text-slate-600 flex items-center">
                        Category:
                        <InfoTooltip
                          text="Applies this discount to all items in this category. Line-item discounts can still override individual items."
                          position="left"
                          size={12}
                        />
                      </span>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={categoryDiscount || ''}
                          onChange={(e) => setCategoryDiscount(group.categories, e.target.value)}
                          className={`w-16 p-1.5 pr-6 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                            hasCategoryDiscount
                              ? 'border-green-500 bg-green-50'
                              : 'border-slate-300 bg-white'
                          }`}
                          placeholder="0"
                        />
                        <Percent size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
                      </div>
                      {hasCategoryDiscount && (
                        <button
                          onClick={() => clearCategoryDiscount(group.categories)}
                          className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Clear category discount"
                        >
                          ×
                        </button>
                      )}
                    </div>

                    {/* Totals */}
                    <div className="text-right">
                      <div className={`font-bold ${colors.text}`}>
                        ${discountedTotal.toLocaleString()}
                      </div>
                      {totalDiscount > 0 && (
                        <div className="text-xs text-red-600">
                          -${totalDiscount.toLocaleString()} discount
                        </div>
                      )}
                    </div>

                    {/* PEPM */}
                    {isHCMDeal && groupPepm && (
                      <div className="text-sm text-purple-600 bg-purple-50 px-2 py-1 rounded">
                        PEPM: ${groupPepm}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Group Content */}
              {isExpanded && (
                <div className="bg-white">
                  {hasSubCategories ? (
                    // Render sub-categories for Products
                    <div className="divide-y divide-slate-100">
                      {Object.entries(group.subCategories).map(([subKey, subCat]) => {
                        const subItems = getSubCategoryItems(subCat.categories);
                        if (subItems.length === 0) return null;

                        const isSubExpanded = expandedSubCategories[subKey];
                        const subListTotal = getSubCategoryListTotal(subCat.categories);
                        const subDiscountedTotal = getSubCategoryDiscountedTotal(subCat.categories);
                        const subDiscount = subListTotal - subDiscountedTotal;
                        const subPepm = getSubCategoryPEPM(subCat.categories);

                        return (
                          <div key={subKey}>
                            {/* Sub-category header */}
                            <button
                              onClick={() => toggleSubCategory(subKey)}
                              className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-slate-50 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span className="w-5 h-5 flex items-center justify-center rounded bg-teal-100 text-teal-600">
                                  {isSubExpanded ? <Minus size={12} /> : <Plus size={12} />}
                                </span>
                                <span className="text-sm font-medium text-slate-700">{subCat.label}</span>
                                <span className="text-xs text-slate-400">({subItems.length})</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-semibold text-slate-700">
                                  ${subDiscountedTotal.toLocaleString()}
                                </span>
                                {subDiscount > 0 && (
                                  <span className="text-xs text-red-600">
                                    -${subDiscount.toLocaleString()}
                                  </span>
                                )}
                                {isHCMDeal && subPepm && (
                                  <span className="text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                                    PEPM: ${subPepm}
                                  </span>
                                )}
                              </div>
                            </button>

                            {/* Sub-category items */}
                            {isSubExpanded && (
                              <div className="bg-slate-50/50">
                                {renderDiscountTable(subItems)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    // Render regular items table
                    renderDiscountTable(groupItems)
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Grand Total Summary */}
      {(() => {
        const arrItems = items.filter(i => !i.isOneTime);
        const oneTimeItems = items.filter(i => i.isOneTime);
        const arrListTotal = arrItems.reduce((sum, item) => sum + (item.listPrice * item.quantity), 0);
        const arrDiscountedTotal = arrItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);
        const arrDiscount = arrListTotal - arrDiscountedTotal;
        const oneTimeListTotal = oneTimeItems.reduce((sum, item) => sum + (item.listPrice * item.quantity), 0);
        const oneTimeDiscountedTotal = oneTimeItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);
        const oneTimeDiscount = oneTimeListTotal - oneTimeDiscountedTotal;

        return (
          <div className="mt-6 bg-slate-800 rounded-xl p-6">
            <div className="space-y-3">
              {/* ARR Section */}
              <div className="flex items-center justify-between text-slate-300">
                <span>ARR (List Price)</span>
                <span className="font-semibold">${arrListTotal.toLocaleString()}</span>
              </div>
              {arrDiscount > 0 && (
                <div className="flex items-center justify-between text-red-400">
                  <span>ARR Discount</span>
                  <span className="font-semibold">-${arrDiscount.toLocaleString()}</span>
                </div>
              )}
              <div className="border-t border-slate-600 pt-3 flex items-center justify-between">
                <div>
                  <div className="text-slate-400 text-sm">Total ARR</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {arrItems.length} recurring items
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-bold text-green-400">
                    ${arrDiscountedTotal.toLocaleString()}
                  </div>
                  {isHCMDeal && (
                    <div className="text-sm text-purple-400 bg-purple-900/30 px-2 py-1 rounded">
                      PEPM: ${(arrDiscountedTotal / (employeeCount * 12)).toFixed(2)}
                    </div>
                  )}
                </div>
              </div>

              {/* One-Time Section */}
              {oneTimeListTotal > 0 && (
                <>
                  <div className="border-t border-slate-600 pt-3 flex items-center justify-between text-orange-300">
                    <span>One-Time (List Price)</span>
                    <span className="font-semibold">${oneTimeListTotal.toLocaleString()}</span>
                  </div>
                  {oneTimeDiscount > 0 && (
                    <div className="flex items-center justify-between text-red-400">
                      <span>One-Time Discount</span>
                      <span className="font-semibold">-${oneTimeDiscount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="text-orange-400 text-sm">Total One-Time</div>
                    <div className="text-xl font-bold text-orange-400">
                      ${oneTimeDiscountedTotal.toLocaleString()}
                    </div>
                  </div>
                </>
              )}

              {/* Grand Total */}
              {oneTimeListTotal > 0 && (
                <div className="border-t border-slate-600 pt-3 flex items-center justify-between">
                  <div className="text-white text-sm font-semibold">Grand Total (ARR + One-Time)</div>
                  <div className="text-xl font-bold text-white">
                    ${(arrDiscountedTotal + oneTimeDiscountedTotal).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default DiscountsStep;
